import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { RefreshCw, Info, Database, ChevronDown, ChevronUp } from 'lucide-react';
import { useLaadpleinMarktdata, type RdwYearRow, type RdwMonthRow } from '@/hooks/useLaadpleinMarktdata';
import { useLaadpleinSyncStatus } from '@/hooks/useLaadpleinSyncStatus';
import { DataSourceBadge } from './DataSourceBadge';

type CatFilter = 'alle' | 'n2' | 'n3' | 'm2' | 'm3';

const CAT_COLORS: Record<string, string> = {
  n2: '#059669',
  n3: '#0891b2',
  m2: '#7c3aed',
  m3: '#c026d3',
};

const CAT_LABELS: Record<string, string> = {
  n2: 'N2 (3,5-12t)',
  n3: 'N3 (>12t)',
  m2: 'M2 (bus ≤5t)',
  m3: 'M3 (bus >5t)',
};

function getCumulatief(row: RdwYearRow, filter: CatFilter): number {
  switch (filter) {
    case 'n2': return row.n2_cumulatief;
    case 'n3': return row.n3_cumulatief;
    case 'm2': return row.m2_cumulatief;
    case 'm3': return row.m3_cumulatief;
    default: return row.n2_cumulatief + row.n3_cumulatief + row.m2_cumulatief + row.m3_cumulatief || row.cumulatief_totaal;
  }
}

function getNieuw(row: RdwYearRow, filter: CatFilter): number {
  switch (filter) {
    case 'n2': return row.n2_nieuw;
    case 'n3': return row.n3_nieuw;
    case 'm2': return row.m2_nieuw;
    case 'm3': return row.m3_nieuw;
    default: return row.n2_nieuw + row.n3_nieuw + row.m2_nieuw + row.m3_nieuw || row.nieuw_totaal;
  }
}

/** Raw data inspection dialog showing actual DB rows */
function RawDataDialog({ monthRows }: { monthRows: RdwMonthRow[] }) {
  const [expanded, setExpanded] = useState(false);
  const displayRows = expanded ? monthRows : monthRows.slice(-24);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="text-xs gap-1">
          <Database className="h-3 w-3" /> Rauwe RDW data
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Rauwe data uit laadplein_marktdata
            <span className="text-xs font-normal text-muted-foreground">({monthRows.length} maanden, bron: RDW Open Data)</span>
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground mb-2">
          Dit zijn de exacte waarden zoals opgeslagen in de database na sync met RDW Open Data (datasets m9d7-ebf2 + 8ys7-d773).
          Elk record = 1 maand. Cumulatief = totaal alle registraties t/m die maand. Nieuw = nieuwe registraties die maand.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="border p-1.5 text-left font-medium">Datum</th>
                <th className="border p-1.5 text-right font-medium">N2+N3 cum.</th>
                <th className="border p-1.5 text-right font-medium" style={{ color: CAT_COLORS.n2 }}>N2 cum.</th>
                <th className="border p-1.5 text-right font-medium" style={{ color: CAT_COLORS.n2 }}>N2 nieuw</th>
                <th className="border p-1.5 text-right font-medium" style={{ color: CAT_COLORS.n3 }}>N3 cum.</th>
                <th className="border p-1.5 text-right font-medium" style={{ color: CAT_COLORS.n3 }}>N3 nieuw</th>
                <th className="border p-1.5 text-right font-medium" style={{ color: CAT_COLORS.m2 }}>M2 cum.</th>
                <th className="border p-1.5 text-right font-medium" style={{ color: CAT_COLORS.m3 }}>M3 cum.</th>
                <th className="border p-1.5 text-left font-medium">Bron</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map(row => {
                const hasBreakdown = row.ev_n2_cumulatief > 0 || row.ev_n3_cumulatief > 0;
                const sumCheck = row.ev_n2_cumulatief + row.ev_n3_cumulatief;
                const mismatch = hasBreakdown && sumCheck !== row.ev_trucks_nl_cumulatief;
                return (
                  <tr key={row.datum} className={mismatch ? 'bg-red-50' : ''}>
                    <td className="border p-1.5 font-mono">{row.datum}</td>
                    <td className="border p-1.5 text-right font-semibold">
                      {row.ev_trucks_nl_cumulatief.toLocaleString()}
                      {mismatch && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <span className="text-red-500 ml-1">!</span>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs">
                              Mismatch: N2({row.ev_n2_cumulatief}) + N3({row.ev_n3_cumulatief}) = {sumCheck} ≠ totaal({row.ev_trucks_nl_cumulatief}). Sync per categorie opnieuw uitvoeren.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </td>
                    <td className="border p-1.5 text-right" style={{ color: CAT_COLORS.n2 }}>{row.ev_n2_cumulatief || '-'}</td>
                    <td className="border p-1.5 text-right text-gray-500">{row.ev_n2_nieuw || '-'}</td>
                    <td className="border p-1.5 text-right" style={{ color: CAT_COLORS.n3 }}>{row.ev_n3_cumulatief || '-'}</td>
                    <td className="border p-1.5 text-right text-gray-500">{row.ev_n3_nieuw || '-'}</td>
                    <td className="border p-1.5 text-right" style={{ color: CAT_COLORS.m2 }}>{row.ev_m2_cumulatief || '-'}</td>
                    <td className="border p-1.5 text-right" style={{ color: CAT_COLORS.m3 }}>{row.ev_m3_cumulatief || '-'}</td>
                    <td className="border p-1.5 text-gray-500 truncate max-w-[120px]">{row.bron_registraties}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {monthRows.length > 24 && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 text-xs"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
            {expanded ? `Toon laatste 24 maanden` : `Toon alle ${monthRows.length} maanden`}
          </Button>
        )}
        <div className="text-xs text-muted-foreground mt-2 border-t pt-2">
          <strong>RDW datasets:</strong> Voertuigen (m9d7-ebf2) gekruist met Brandstof (8ys7-d773) op kenteken.
          Filter: europese_voertuigcategorie in ('N2','N3','M2','M3') AND brandstof_omschrijving = 'Elektriciteit'.
          {' '}<span className="text-red-500">Rode rijen</span> = N2+N3 breakdown klopt niet met totaal (hernieuwde sync nodig).
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MarktTab() {
  const { yearRows, monthRows, latestMonth, isLive, isLoading, refresh } = useLaadpleinMarktdata();
  const { getStatus } = useLaadpleinSyncStatus();
  const rdwStatus = getStatus('rdw');
  const [catFilter, setCatFilter] = useState<CatFilter>('alle');

  const latest = latestMonth;
  const maxCum = yearRows.length > 0
    ? Math.max(...yearRows.map(r => getCumulatief(r, catFilter)), 1)
    : 1;

  return (
    <div>
      {/* Sync status + controls */}
      <div className="flex items-center justify-between gap-3 mb-4 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
        <DataSourceBadge bron={isLive ? 'rdw' : 'demo'} isLive={isLive} lastSync={rdwStatus.lastSync} />
        <div className="flex gap-1 flex-wrap">
          {['N2', 'N3', 'M2', 'M3'].map(cat => (
            <Button
              key={cat}
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={() => refresh.mutate(cat)}
              disabled={refresh.isPending}
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${refresh.isPending ? 'animate-spin' : ''}`} />
              {cat}
            </Button>
          ))}
          <RawDataDialog monthRows={monthRows} />
        </div>
      </div>

      {/* Stat cards */}
      {latest && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard
            label="EV trucks (N2+N3)"
            value={latest.ev_trucks_nl_cumulatief.toLocaleString()}
            sub={`+${latest.ev_trucks_nl_nieuw_maand} laatste maand`}
            tooltip={`RDW Open Data, datum ${latest.datum}. Cumulatief N2+N3 met brandstof=Elektriciteit.`}
            color="emerald"
          />
          <StatCard
            label="N2 (3,5-12 ton)"
            value={latest.ev_n2_cumulatief > 0 ? latest.ev_n2_cumulatief.toLocaleString() : '–'}
            sub={latest.ev_n2_cumulatief > 0 ? `+${latest.ev_n2_nieuw} laatste maand` : 'Klik Sync N2'}
            tooltip="Middelzware trucks: eSprinter, E-Transit, eCanter. SPULA-relevant."
            color="emerald"
          />
          <StatCard
            label="N3 (>12 ton)"
            value={latest.ev_n3_cumulatief > 0 ? latest.ev_n3_cumulatief.toLocaleString() : '–'}
            sub={latest.ev_n3_cumulatief > 0 ? `+${latest.ev_n3_nieuw} laatste maand` : 'Klik Sync N3'}
            tooltip="Zware trucks: DAF CF/XF Electric, Volvo FH Electric, Scania. SPULA-relevant."
            color="blue"
          />
          <StatCard
            label="M2+M3 (bussen)"
            value={(latest.ev_m2_cumulatief + latest.ev_m3_cumulatief) > 0
              ? (latest.ev_m2_cumulatief + latest.ev_m3_cumulatief).toLocaleString()
              : '–'}
            sub={(latest.ev_m2_cumulatief + latest.ev_m3_cumulatief) > 0 ? 'Elektrische bussen' : 'Klik Sync M2/M3'}
            tooltip="Elektrische bussen. M2 ≤5t, M3 >5t. Relevant voor ZE-zones en OV."
            color="purple"
          />
        </div>
      )}

      {!latest && !isLoading && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-800">
          Geen RDW data gevonden. Klik per categorie op Sync om live data op te halen.
        </div>
      )}

      {/* Category filter */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-gray-500 mr-1">Filter:</span>
        {(['alle', 'n2', 'n3', 'm2', 'm3'] as CatFilter[]).map(f => (
          <Button
            key={f}
            variant="ghost"
            size="sm"
            onClick={() => setCatFilter(f)}
            className={`text-xs h-7 ${catFilter === f ? 'bg-emerald-100 text-emerald-800 font-bold' : 'text-gray-500'}`}
          >
            {f === 'alle' ? 'Alle (N2+N3+M2+M3)' : CAT_LABELS[f]}
          </Button>
        ))}
      </div>

      {/* Stacked bar chart */}
      {yearRows.length > 0 && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-sm font-medium text-gray-700">EV-registraties per jaar</div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger><Info className="h-3.5 w-3.5 text-gray-400" /></TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">
                    100% RDW Open Data. Voertuigen met brandstof_omschrijving='Elektriciteit' uit dataset 8ys7-d773,
                    gekruist met europese_voertuigcategorie uit dataset m9d7-ebf2. Geen schattingen of prognoses.
                    Klik "Rauwe RDW data" om elke maand-rij te inspecteren.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="space-y-1.5">
              {yearRows.map(row => {
                const cum = getCumulatief(row, catFilter);
                const nieuw = getNieuw(row, catFilter);
                const barWidth = Math.max(2, (cum / maxCum) * 100);

                const showStacked = catFilter === 'alle' && (row.n2_cumulatief > 0 || row.n3_cumulatief > 0 || row.m2_cumulatief > 0 || row.m3_cumulatief > 0);
                const n2Pct = showStacked && cum > 0 ? (row.n2_cumulatief / cum) * 100 : 0;
                const n3Pct = showStacked && cum > 0 ? (row.n3_cumulatief / cum) * 100 : 0;
                const m2Pct = showStacked && cum > 0 ? (row.m2_cumulatief / cum) * 100 : 0;
                const m3Pct = showStacked && cum > 0 ? (row.m3_cumulatief / cum) * 100 : 0;

                return (
                  <TooltipProvider key={row.jaar}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 text-xs cursor-default">
                          <span className="w-10 font-mono font-medium text-gray-700">{row.jaar}</span>
                          <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden relative">
                            {showStacked ? (
                              <div className="h-full flex" style={{ width: `${barWidth}%` }}>
                                <div className="h-full" style={{ width: `${n2Pct}%`, backgroundColor: CAT_COLORS.n2 }} />
                                <div className="h-full" style={{ width: `${n3Pct}%`, backgroundColor: CAT_COLORS.n3 }} />
                                <div className="h-full" style={{ width: `${m2Pct}%`, backgroundColor: CAT_COLORS.m2 }} />
                                <div className="h-full" style={{ width: `${m3Pct}%`, backgroundColor: CAT_COLORS.m3 }} />
                              </div>
                            ) : (
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${barWidth}%`,
                                  backgroundColor: catFilter === 'alle' ? CAT_COLORS.n2 : CAT_COLORS[catFilter],
                                }}
                              />
                            )}
                            <span
                              className="absolute inset-y-0 left-2 flex items-center text-xs font-medium"
                              style={{ color: barWidth > 25 ? 'white' : '#374151' }}
                            >
                              {cum.toLocaleString()}
                            </span>
                          </div>
                          <span className="w-16 text-right text-gray-500">+{nieuw.toLocaleString()}</span>
                          <span className="w-8 text-right text-gray-400">RDW</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs max-w-sm">
                        <div className="font-medium mb-1">{row.jaar} — RDW Open Data</div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                          <span>N2 cumulatief:</span><span className="font-mono">{row.n2_cumulatief.toLocaleString()}</span>
                          <span>N2 nieuw:</span><span className="font-mono">{row.n2_nieuw.toLocaleString()}</span>
                          <span>N3 cumulatief:</span><span className="font-mono">{row.n3_cumulatief.toLocaleString()}</span>
                          <span>N3 nieuw:</span><span className="font-mono">{row.n3_nieuw.toLocaleString()}</span>
                          <span>M2 cumulatief:</span><span className="font-mono">{row.m2_cumulatief.toLocaleString()}</span>
                          <span>M3 cumulatief:</span><span className="font-mono">{row.m3_cumulatief.toLocaleString()}</span>
                          <span className="font-semibold border-t pt-0.5">N2+N3 totaal:</span>
                          <span className="font-mono font-semibold border-t pt-0.5">{row.cumulatief_totaal.toLocaleString()}</span>
                        </div>
                        {row.n2_cumulatief === 0 && row.n3_cumulatief === 0 && (
                          <div className="text-amber-600 mt-1">Breakdown niet beschikbaar. Sync N2 en N3 apart voor details.</div>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>

            <div className="flex gap-4 mt-3 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: CAT_COLORS.n2 }} /> N2 (3,5-12t)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: CAT_COLORS.n3 }} /> N3 (&gt;12t)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: CAT_COLORS.m2 }} /> M2/M3 (bus)
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vehicle categories reference */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-sm font-medium text-gray-700">RDW Voertuigcategorieen</div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger><Info className="h-3.5 w-3.5 text-gray-400" /></TooltipTrigger>
                <TooltipContent className="max-w-sm text-xs">
                  Europese voertuigcategorieen uit RDW Open Data (dataset m9d7-ebf2). Gefilterd op brandstof_omschrijving = 'Elektriciteit' uit dataset 8ys7-d773.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-1.5 pr-3 text-gray-500 font-medium">Cat</th>
                <th className="text-left py-1.5 pr-3 text-gray-500 font-medium">Type</th>
                <th className="text-left py-1.5 pr-3 text-gray-500 font-medium">Gewicht</th>
                <th className="text-center py-1.5 pr-3 text-gray-500 font-medium">SPULA</th>
                <th className="text-left py-1.5 text-gray-500 font-medium">Toelichting</th>
              </tr>
            </thead>
            <tbody>
              {[
                { code: 'N2', col: CAT_COLORS.n2, type: 'Middelzwaar commercieel', gew: '3,5 - 12 ton', spula: true, txt: 'Distributietrucks (e-Sprinter, E-Transit, eCanter). Primaire SPULA-doelgroep.' },
                { code: 'N3', col: CAT_COLORS.n3, type: 'Zwaar commercieel', gew: '> 12 ton', spula: true, txt: 'Trekkers, zware vrachtwagens (DAF CF/XF, Volvo FH, Scania). Hoogste laadvermogen nodig.' },
                { code: 'N1', col: '#9ca3af', type: 'Licht commercieel', gew: '< 3,5 ton', spula: false, txt: 'Bestelwagens. Niet SPULA-relevant, niet opgehaald (1M+ records).' },
                { code: 'M2', col: CAT_COLORS.m2, type: 'Kleine bus', gew: '≤ 5 ton, 9+ zitpl.', spula: false, txt: 'Minibussen. Relevant voor ZE-zone personenvervoer.' },
                { code: 'M3', col: CAT_COLORS.m3, type: 'Grote bus', gew: '> 5 ton, 9+ zitpl.', spula: false, txt: 'OV-bussen. Relevant voor OV-electrificatie en depotladen.' },
              ].map(cat => (
                <tr key={cat.code} className="border-b last:border-0">
                  <td className="py-1.5 pr-3 font-mono font-bold" style={{ color: cat.col }}>{cat.code}</td>
                  <td className="py-1.5 pr-3">{cat.type}</td>
                  <td className="py-1.5 pr-3 text-gray-500">{cat.gew}</td>
                  <td className="py-1.5 pr-3 text-center">{cat.spula ? <span className="text-emerald-600 font-bold">&#10003;</span> : <span className="text-gray-400">-</span>}</td>
                  <td className="py-1.5 text-gray-600">{cat.txt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, sub, color, tooltip }: { label: string; value: string; sub?: string; color: string; tooltip?: string }) {
  const bgMap: Record<string, string> = { emerald: "bg-emerald-50", blue: "bg-blue-50", purple: "bg-purple-50", gray: "bg-gray-50" };
  const tcMap: Record<string, string> = { emerald: "text-emerald-700", blue: "text-blue-700", purple: "text-purple-700", gray: "text-gray-700" };
  return (
    <div className={`${bgMap[color] || "bg-gray-50"} rounded-xl p-3`}>
      <div className="flex items-start justify-between">
        <div className={`text-xl font-bold ${tcMap[color] || "text-gray-700"}`}>{value}</div>
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger><Info className="h-3 w-3 text-gray-400" /></TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">{tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
}
